import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TierBadge } from "@/components/waste/tier-badge";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { EventLink } from "@/components/admin/event-link";
import {
  FlaggedCapturesDialog,
  type FlaggedCapturesTarget,
} from "@/components/admin/flagged-captures-dialog";
import { cn } from "@/lib/utils";
import { Column, Pagination, Table, Td } from "@/components/admin/table";
import { usePaged } from "@/components/admin/use-paged";

/**
 * Platform-admin labelling dashboard (POS2-1057, CP4).
 *
 * Per `design/labelling-admin.html`, built with the design system per DESIGN_PROMPT
 * — which means the mock's boxed KPI cards keep their shape (they are data tiles,
 * not grouped form sections) but lose their shadows, and its Google Fonts `<link>`
 * is not carried across (POS2-1066: blocked on customer networks).
 *
 * Every rate is nullable on purpose. A period with no work has no correct rate, and
 * showing "0%" would read as "nothing was correct" rather than "nothing happened".
 */
const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
const BASE = `${API_URL}/api/v1/admin/labelling`;

type Period = "today" | "7d" | "30d";

type Kpis = {
  period_from: string;
  period_to: string;
  items_labelled: number;
  hours_logged: number;
  items_per_hour: number | null;
  pending: number;
  correct_rate: number | null;
  correct2_rate: number | null;
  relabel_rate: number | null;
  discard_rate: number | null;
  cant_verify_rate: number | null;
  heavy_backlog: number;
  heavy_second_reviews: number;
};

type LabellerRow = {
  labeller_id: string;
  email: string;
  items: number;
  hours: number;
  items_per_hour: number | null;
  correct_rate: number | null;
  correct2_rate: number | null;
  relabel_rate: number | null;
  discard_rate: number | null;
  cant_verify_rate: number | null;
  agreement: number | null;
  is_on_shift: boolean;
  shift_started_at: string | null;
  shift_items: number;
  shift_target: number | null;
  last_shift_ended_at: string | null;
};

type ShiftLogRow = {
  shift_id: string;
  email: string;
  started_at: string;
  ended_at: string | null;
  end_reason: string | null;
  duration_seconds: number;
  items: number;
  items_per_hour: number | null;
  target_items: number;
};

type SiteRow = {
  site_id: string;
  site_name: string;
  org_name: string;
  events_labelled: number;
  site_labelled_events: number;
  pending: number;
  correct_rate: number | null;
  correct2_rate: number | null;
  relabel_rate: number | null;
  discard_rate: number | null;
  cant_verify_rate: number | null;
  top_relabelled_item: string | null;
  needs_training_data: boolean;
};

type DeviceRow = {
  device_serial: string | null;
  device_name: string | null;
  site_id: string;
  site_name: string;
  org_name: string;
  flags: number;
  // Exhaustive and summing to `flags` (POS2-1123). Two counts rather than one "most common
  // reason", because a dirty lens and a wash-up camera photographing mixed scrapings are
  // different problems for different people and the collapsed column ranked them alike.
  capture_fault_flags: number;
  not_nameable_flags: number;
  last_flagged_at: string;
};

type ExceptionRow = {
  waste_event_id: string;
  org_id: string;
  org_name: string;
  site_name: string;
  item_weight: number;
  classification_level: number | null;
  enqueued_at: string;
  first_action: string;
  first_label: string | null;
  origin_labeller_email: string;
  second_action: string;
  second_label: string | null;
  second_labeller_email: string;
};

const PERIODS: { value: Period; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
];

/**
 * How many shifts the log asks for, and the number the section note quotes.
 *
 * `GET /shifts` bounds itself (default 50, max 200) and the frontend used to send nothing,
 * so the log was already capped. Paginating a capped list would otherwise read as "these
 * are all the shifts" — the request and the sentence come from the same constant so they
 * cannot drift apart.
 */
const SHIFT_LOG_LIMIT = 50;

function useAdminQuery<T>(path: string, params?: Record<string, string | number>) {
  const headers = useApiHeaders();
  const query = params
    ? `?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]))}`
    : "";
  return useQuery<T>({
    queryKey: ["admin-labelling", path, params],
    queryFn: async () => {
      const res = await fetch(`${BASE}/${path}${query}`, { headers });
      if (!res.ok) throw new Error(`Failed to load ${path}: ${res.status}`);
      return (await res.json()).data as T;
    },
  });
}

const LABELLER_COLUMNS: Column[] = [
  { label: "Labeller" },
  { label: "Shift progress" },
  { label: "Items", numeric: true },
  { label: "Hours", numeric: true },
  { label: "Items / hr", numeric: true },
  { label: "Correct", numeric: true },
  { label: "Correct2", numeric: true },
  { label: "Relabel", numeric: true },
  { label: "Discard", numeric: true },
  { label: "Can't verify", numeric: true },
  { label: "Split" },
  { label: "Agreement", numeric: true },
  { label: "Status" },
];

const EXCEPTION_COLUMNS: Column[] = [
  { label: "Event" },
  { label: "Site" },
  { label: "Weight", numeric: true },
  { label: "Level" },
  { label: "First call" },
  { label: "Second call" },
  { label: "Raised" },
  { label: "", srLabel: "Actions" },
];

const SHIFT_COLUMNS: Column[] = [
  { label: "Date" },
  { label: "Labeller" },
  { label: "Clock in", numeric: true },
  { label: "Clock out", numeric: true },
  { label: "Duration", numeric: true },
  { label: "Items", numeric: true },
  { label: "Items / hr", numeric: true },
  { label: "Target" },
];

const SITE_COLUMNS: Column[] = [
  { label: "Site" },
  { label: "Events labelled", numeric: true },
  { label: "Site-labelled", numeric: true },
  { label: "Pending", numeric: true },
  { label: "Correct", numeric: true },
  { label: "Correct2", numeric: true },
  { label: "Relabel", numeric: true },
  { label: "Discard", numeric: true },
  { label: "Can't verify", numeric: true },
  { label: "Split" },
  { label: "Top relabelled item" },
  { label: "Model health" },
];

const DEVICE_COLUMNS: Column[] = [
  { label: "Device" },
  { label: "Site" },
  { label: "Flags", numeric: true },
  { label: "Capture faults", numeric: true },
  { label: "Not nameable", numeric: true },
  { label: "Last flagged", numeric: true },
];

export default function AdminLabellingPage() {
  const [period, setPeriod] = useState<Period>("7d");
  // The Devices row whose captures are open. Null closes the dialog by unmounting it, which
  // is what defers its fetch to the moment it opens (POS2-1123).
  const [flagsFor, setFlagsFor] = useState<FlaggedCapturesTarget | null>(null);
  const headers = useApiHeaders();

  const kpis = useAdminQuery<Kpis>("kpis", { period });
  const labellers = useAdminQuery<LabellerRow[]>("labellers", { period });
  const shifts = useAdminQuery<ShiftLogRow[]>("shifts", { limit: SHIFT_LOG_LIMIT });
  const sites = useAdminQuery<SiteRow[]>("sites", { period });
  const devices = useAdminQuery<DeviceRow[]>("devices", { period });
  const exceptions = useAdminQuery<ExceptionRow[]>("exceptions");

  const shiftPage = usePaged(shifts.data ?? []);
  const sitePage = usePaged(sites.data ?? []);
  const devicePage = usePaged(devices.data ?? []);
  const exceptionPage = usePaged(exceptions.data ?? []);

  async function exportCsv() {
    const res = await fetch(`${BASE}/export?period=${period}`, { headers });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `labelling-${period}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          {/* The Dashboard and Explore page-title treatment, which is the app's current
              convention. DESIGN_PROMPT still specifies `text-sm font-medium` for a page
              title; every recently built page has moved past it, and this one was the
              outlier at text-sm. */}
          <h1 className="font-display text-xl font-extrabold tracking-tight text-gray-900">
            Labelling
          </h1>
          {/* The period in words, per the mock. It comes from the KPI response rather than
              from the toggle, so the dates always name the window the numbers below were
              computed over — before it loads there is no window to name, so the sentence
              falls back to what the page is. */}
          <p className="mt-1 text-sm text-gray-500">
            {kpis.data
              ? periodRange(kpis.data.period_from, kpis.data.period_to)
              : "Internal label verification"}{" "}
            · shift target {labellers.data?.[0]?.shift_target ?? 500} items per labeller
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
            {PERIODS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setPeriod(option.value)}
                aria-pressed={period === option.value}
                className={cn(
                  "rounded px-3 py-1 text-sm font-medium transition-colors duration-150",
                  period === option.value
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Three across rather than four since POS2-1083 added a ninth tile: nine over
          four columns leaves the last one alone on its own row. */}
      {kpis.isLoading ? (
        <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3">
          {Array.from({ length: 9 }, (_, index) => (
            <Skeleton key={index} className="h-[74px] rounded-lg" />
          ))}
        </div>
      ) : (
        kpis.data && (
          <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3">
            <Kpi label="Items labelled" value={num(kpis.data.items_labelled)} />
            <Kpi label="Hours logged" value={`${kpis.data.hours_logged.toFixed(1)} h`} />
            <Kpi label="Items per hour" value={rate(kpis.data.items_per_hour, 0)} />
            <Kpi label="Pending queue" value={num(kpis.data.pending)} />
            {/* POS2-1206: both figures in one tile — Correct is what a labeller
                confirmed outright, Correct2 what the model was not contradicted on.
                Reading them apart from each other is the mistake the pair prevents. */}
            <Kpi
              label="Correct rate"
              value={pctPair(kpis.data.correct_rate, kpis.data.correct2_rate)}
              hint="Correct / Correct2"
              tone="emerald"
            />
            <Kpi label="Relabel rate" value={pct(kpis.data.relabel_rate)} tone="orange" />
            <Kpi label="Discard rate" value={pct(kpis.data.discard_rate)} tone="red" />
            {/* Toneless: an unusable capture is not a verdict and not a fault of the
                labeller who reported it. POS2-1206: Correct, Correct2, Relabel, Discard
                and this one are five disjoint shares of the same denominator. */}
            <Kpi label="Can't verify rate" value={pct(kpis.data.cant_verify_rate)} />
            <Kpi
              label="Heavy backlog · target 0 daily"
              value={num(kpis.data.heavy_backlog)}
              hint={`incl. ${kpis.data.heavy_second_reviews} 2nd reviews`}
            />
          </div>
        )
      )}

      <Section
        title="Labellers"
        note="Rates are share of the items each labeller processed in the period"
      >
        <Table
          isLoading={labellers.isLoading}
          columns={LABELLER_COLUMNS}
          rows={labellers.data ?? []}
          empty="No labellers yet"
          renderRow={(
            row: LabellerRow,
            [
              who,
              progress,
              items,
              hours,
              iph,
              correct,
              correct2,
              relabel,
              discard,
              cantVerify,
              split,
              agree,
              status,
            ],
          ) => (
            <tr key={row.labeller_id} className="border-b border-black/[0.06] last:border-b-0">
              <Td column={who} className="font-medium text-gray-900">
                {row.email}
              </Td>
              <Td column={progress}>
                {row.is_on_shift && row.shift_target ? (
                  <Progress value={row.shift_items} target={row.shift_target} />
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </Td>
              <Td column={items}>{num(row.items)}</Td>
              <Td column={hours}>{row.hours.toFixed(1)} h</Td>
              <Td column={iph}>{rate(row.items_per_hour, 0)}</Td>
              <Td column={correct} className="text-emerald-700">
                {pct(row.correct_rate)}
              </Td>
              <Td column={correct2} className="text-emerald-600">
                {pct(row.correct2_rate)}
              </Td>
              <Td column={relabel} className="text-orange-600">
                {pct(row.relabel_rate)}
              </Td>
              <Td column={discard} className="text-red-600">
                {pct(row.discard_rate)}
              </Td>
              <Td column={cantVerify} className="text-gray-500">
                {pct(row.cant_verify_rate)}
              </Td>
              <Td column={split}>
                <Split
                  correct={row.correct_rate}
                  correct2={row.correct2_rate}
                  relabel={row.relabel_rate}
                  discard={row.discard_rate}
                  cantVerify={row.cant_verify_rate}
                />
              </Td>
              <Td column={agree}>{pct(row.agreement)}</Td>
              <Td column={status}>
                {row.is_on_shift ? (
                  <Tag tone="emerald">On shift</Tag>
                ) : (
                  <Tag tone="neutral">
                    {row.last_shift_ended_at
                      ? `Off shift · last ${shortDate(row.last_shift_ended_at)}`
                      : "Never on shift"}
                  </Tag>
                )}
              </Td>
            </tr>
          )}
        />
        <Footnote>
          Correct2 is a relabel that did not contradict the model: one that only took its own L3
          deeper into an L4 item beneath that subcategory, or one that labelled an event the model
          left blank. Those relabels no longer count under Relabel, so Correct, Correct2, Relabel,
          Discard and Can’t verify are five disjoint shares that divide the period — and how often
          the model was not contradicted is Correct + Correct2. The Split bar draws the same five.
          Hours = clocked time between Start shift and End shift. Agreement = share of a labeller's
          Correct calls on heavy items confirmed by a blind second review; a review still awaiting
          its second opinion is not counted either way. Can't verify is an item the capture made
          unjudgeable — it counts toward the shift target, because a dirty camera is not the
          labeller's fault, and it changes nothing on the event. Labellers never see review order or
          each other's answers.
        </Footnote>
      </Section>

      <Section
        title="Exceptions"
        note="Second review disagreed · the first labeller's label stands, so this flags the disagreement rather than deciding it"
      >
        <Table
          isLoading={exceptions.isLoading}
          columns={EXCEPTION_COLUMNS}
          rows={exceptionPage.page}
          empty="No disagreements outstanding"
          footer={exceptionPage.bar && <Pagination {...exceptionPage.bar} />}
          renderRow={(
            row: ExceptionRow,
            [event, site, weight, level, first, second, raised, actions],
          ) => (
            <tr key={row.waste_event_id} className="border-b border-black/[0.06] last:border-b-0">
              <Td column={event}>
                <EventLink eventId={row.waste_event_id} orgId={row.org_id} orgName={row.org_name} />
              </Td>
              <Td column={site}>{row.site_name}</Td>
              {/* The one numeric cell carrying a unit, so the only one that can split
                  across two lines when something else in the row claims width. */}
              <Td column={weight} className="whitespace-nowrap">
                {row.item_weight.toFixed(2)} kg
              </Td>
              <Td column={level}>
                <TierBadge level={row.classification_level} />
              </Td>
              {/* Both calls name the label they refer to, or the row says two labellers
                  disagreed without saying about what. The first call is a Correct, so its
                  label is the one it was shown; the second call's is the replacement it
                  proposed — and a Discard proposes none. */}
              <Td column={first}>
                <Call action={row.first_action} label={row.first_label} />
                <span className="block text-xs text-gray-400">{row.origin_labeller_email}</span>
              </Td>
              <Td column={second}>
                <Call action={row.second_action} label={row.second_label} />
                <span className="block text-xs text-gray-400">{row.second_labeller_email}</span>
              </Td>
              <Td column={raised} className="text-gray-500">
                {shortDate(row.enqueued_at)}
              </Td>
              <Td column={actions}>
                <ResolveButton
                  eventId={row.waste_event_id}
                  onResolved={() => exceptions.refetch()}
                />
              </Td>
            </tr>
          )}
        />
        <Footnote>
          The first labeller's label stands, and the second review scores them rather than replacing
          it — so a row here is a disagreement worth reading, not a label waiting to be decided.
          Resolving records that an admin has looked. If the label really is wrong, correct it on
          the event, where the change is logged.
        </Footnote>
      </Section>

      <Section
        title="Shift log"
        note={`Most recent ${SHIFT_LOG_LIMIT} shifts · clock in and out from the labeller app · times in ${DISPLAY_TIME_ZONE}`}
      >
        <Table
          isLoading={shifts.isLoading}
          columns={SHIFT_COLUMNS}
          rows={shiftPage.page}
          empty="No shifts yet"
          footer={shiftPage.bar && <Pagination {...shiftPage.bar} />}
          renderRow={(
            row: ShiftLogRow,
            [date, who, clockIn, clockOut, dur, items, iph, target],
          ) => (
            <tr key={row.shift_id} className="border-b border-black/[0.06] last:border-b-0">
              {/* The date is its own column, so the clock columns carry times only. Both
                  used to render a full date *and* time, which repeated each row's date
                  twice and made two of the widest columns out of the narrowest values. */}
              <Td column={date} className="text-gray-500">
                {shortDate(row.started_at)}
              </Td>
              <Td column={who} className="font-medium text-gray-900">
                {row.email}
              </Td>
              <Td column={clockIn} className="text-gray-500">
                {clockTime(row.started_at)}
              </Td>
              {/* The badge sits *before* the time, not after it. This column is
                  right-aligned, so a trailing badge took the right edge and pushed its
                  own row's timestamp left — the clock-out times stopped lining up with
                  each other, and only the auto-ended rows were out. Leading it keeps
                  every timestamp on the same edge, and "In progress" lands where the
                  timestamps are rather than beside them. */}
              <Td column={clockOut} className="text-gray-500">
                {row.ended_at ? (
                  <span className="inline-flex items-center justify-end gap-1.5">
                    {row.end_reason === "auto_idle" && <Tag tone="orange">Auto-ended</Tag>}
                    {clockTime(row.ended_at)}
                  </span>
                ) : (
                  <Tag tone="blue">In progress</Tag>
                )}
              </Td>
              <Td column={dur}>{duration(row.duration_seconds)}</Td>
              <Td column={items}>{num(row.items)}</Td>
              <Td column={iph}>{rate(row.items_per_hour, 0)}</Td>
              <Td column={target}>
                <Tag tone={row.items >= row.target_items ? "emerald" : "neutral"}>
                  {row.items} / {row.target_items}
                </Tag>
              </Td>
            </tr>
          )}
        />
        <Footnote>
          Target = {shifts.data?.[0]?.target_items ?? 500} items per shift. The heavy backlog —
          first plus second reviews, weight descending — sits at the top of every labeller's queue
          and must clear to zero each day before the recommended feed (L4 to L1) opens. “Each day”
          is a UTC day, which is also when a second review owed becomes a rollover — so for a
          labelling team working outside UTC the daily deadline falls at a local hour worth checking
          against their shift pattern.
        </Footnote>
      </Section>

      <Section
        title="Sites"
        note="Rates are share of the site's labelled events · a high relabel rate means the model is weak on that menu"
      >
        <Table
          isLoading={sites.isLoading}
          columns={SITE_COLUMNS}
          rows={sitePage.page}
          empty="No labelled events yet"
          footer={sitePage.bar && <Pagination {...sitePage.bar} />}
          renderRow={(
            row: SiteRow,
            [
              site,
              labelled,
              siteLabelled,
              pending,
              correct,
              correct2,
              relabel,
              discard,
              cantVerify,
              split,
              top,
              health,
            ],
          ) => (
            <tr key={row.site_id} className="border-b border-black/[0.06] last:border-b-0">
              <Td column={site}>
                <span className="font-medium text-gray-900">{row.site_name}</span>
                <span className="block text-xs text-gray-400">{row.org_name}</span>
              </Td>
              <Td column={labelled}>{num(row.events_labelled)}</Td>
              <Td column={siteLabelled} className="text-gray-500">
                {num(row.site_labelled_events)}
              </Td>
              <Td column={pending}>{num(row.pending)}</Td>
              <Td column={correct} className="text-emerald-700">
                {pct(row.correct_rate)}
              </Td>
              <Td column={correct2} className="text-emerald-600">
                {pct(row.correct2_rate)}
              </Td>
              <Td column={relabel} className="text-orange-600">
                {pct(row.relabel_rate)}
              </Td>
              <Td column={discard} className="text-red-600">
                {pct(row.discard_rate)}
              </Td>
              <Td column={cantVerify} className="text-gray-500">
                {pct(row.cant_verify_rate)}
              </Td>
              <Td column={split}>
                <Split
                  correct={row.correct_rate}
                  correct2={row.correct2_rate}
                  relabel={row.relabel_rate}
                  discard={row.discard_rate}
                  cantVerify={row.cant_verify_rate}
                />
              </Td>
              <Td column={top} className="text-gray-500">
                {row.top_relabelled_item ?? "—"}
              </Td>
              <Td column={health}>
                {row.needs_training_data ? (
                  <Tag tone="orange">Needs training data</Tag>
                ) : (
                  <Tag tone="emerald">Healthy</Tag>
                )}
              </Td>
            </tr>
          )}
        />
        <Footnote>
          Correct2 is a relabel that did not contradict the model: one that only took its own L3
          deeper into an L4 item beneath that subcategory, or one that labelled an event the model
          left blank. Those relabels no longer count under Relabel, so Correct, Correct2, Relabel,
          Discard and Can’t verify are five disjoint shares that divide the period — and how often
          the model was not contradicted is Correct + Correct2. The Split bar draws the same five.
          “Site-labelled” is AI-classified events the site’s own users confirmed or corrected on
          Transactions in this period — not labeller work, and not events an import or the food map
          confirmed. A labeller never writes that flag, so the two count columns cannot overlap. The
          two audiences are pointed at the same working set by design, so a site verifying its own
          waste is the reason a labeller occasionally loses a card to a chef; the chef wins, and the
          time the labeller lost is deliberately not measured.
        </Footnote>
      </Section>

      <Section
        title="Devices"
        note="Captures labellers could not use · a device climbing this table is a camera fault, not a queue problem"
      >
        <Table
          isLoading={devices.isLoading}
          columns={DEVICE_COLUMNS}
          rows={devicePage.page}
          empty="No unusable captures in this period"
          footer={devicePage.bar && <Pagination {...devicePage.bar} />}
          renderRow={(row: DeviceRow, [device, site, flags, faults, nameable, last]) => (
            <tr
              key={`${row.device_serial ?? "unattributed"}-${row.site_name}`}
              className="border-b border-black/[0.06] last:border-b-0"
            >
              <Td column={device}>
                <span className="font-mono text-xs text-gray-700">
                  {row.device_serial ?? "No device recorded"}
                </span>
                {row.device_name && (
                  <span className="block text-xs text-gray-400">{row.device_name}</span>
                )}
              </Td>
              <Td column={site}>
                <span className="text-gray-700">{row.site_name}</span>
                <span className="block text-xs text-gray-400">{row.org_name}</span>
              </Td>
              <Td column={flags}>
                <FlagCount row={row} onOpen={setFlagsFor} />
              </Td>
              <Td column={faults} className="text-gray-500">
                {num(row.capture_fault_flags)}
              </Td>
              <Td column={nameable} className="text-gray-500">
                {num(row.not_nameable_flags)}
              </Td>
              <Td column={last} className="text-gray-500">
                {shortDate(row.last_flagged_at)}
              </Td>
            </tr>
          )}
        />
        <Footnote>
          A row here is one camera at one site. Obstructed frames and captures that never load are
          capture faults — someone goes and looks at the camera; "not nameable" is a photo that
          arrived fine and still could not be judged, which is a model-training signal rather than
          an engineering one. The two counts are exhaustive, so they sum to Flags. Open a flag count
          to see the captures behind it.
        </Footnote>
      </Section>

      {flagsFor && (
        <FlaggedCapturesDialog
          target={flagsFor}
          period={period}
          onClose={() => setFlagsFor(null)}
        />
      )}
    </div>
  );
}

/**
 * The flag count, as a way into the captures behind it (POS2-1123).
 *
 * A link only where there is a serial to address. `labelling_admin_devices` keeps a
 * NULL-serial row so its counts cannot disagree with the flag rate above, and the endpoint is
 * keyed on `(serial, site_id)` — so that row has nothing to open. It reads "No device
 * recorded" in the Device column already, which is why a plain count there is legible rather
 * than mysterious. Every flaggable event should name a device (the queue serves only
 * `classification_source = 'ai'`, and a manual log is stamped `manual` and never served), so
 * this is expected to be unreachable in practice — but visible on screen if it is not.
 */
function FlagCount({
  row,
  onOpen,
}: {
  row: DeviceRow;
  onOpen: (target: FlaggedCapturesTarget) => void;
}) {
  // Narrowed here rather than asserted in the dialog: the target carries a non-null serial by
  // construction, so nothing downstream has to handle a row it cannot address.
  const serial = row.device_serial;
  if (!serial) return <>{num(row.flags)}</>;
  return (
    <button
      type="button"
      onClick={() =>
        onOpen({
          device_serial: serial,
          device_name: row.device_name,
          site_id: row.site_id,
          site_name: row.site_name,
          flags: row.flags,
        })
      }
      title="See the captures behind this count"
      className="text-blue-600 hover:text-blue-700 hover:underline"
    >
      {num(row.flags)}
    </button>
  );
}

/**
 * The app's name for an action, rather than the stored value.
 *
 * Capitalised because that is what every other surface calls them — the workstation's
 * buttons, its end-of-shift stats, and this page's own KPI tiles and column headers. This
 * row was the one place rendering the raw column.
 *
 * Only these three reach this queue: a second review is owed by a Correct, and a pass-2
 * Can't verify supersedes rather than disagreeing. Anything else — the "—" standing in for
 * an action that was undone — passes through as it is.
 */
const ACTION_NAMES: Record<string, string> = {
  correct: "Correct",
  relabel: "Relabel",
  discard: "Discard",
};

/** One labeller's answer and the label it refers to. A Discard names no label. */
function Call({ action, label }: { action: string; label: string | null }) {
  return (
    <span className="text-gray-700">
      {ACTION_NAMES[action] ?? action}
      {label && <span className="text-gray-400"> · {label}</span>}
    </span>
  );
}

function ResolveButton({ eventId, onResolved }: { eventId: string; onResolved: () => void }) {
  const headers = useApiHeaders();
  const [busy, setBusy] = useState(false);
  // A resolve that 404s used to look exactly like one that worked: the row vanished on the
  // refetch either way, because the refetch was also unchecked. Two things a resolve can
  // hit for real — the row already resolved in another tab, and the event id no longer
  // matching a pending disagreement — and both are worth seeing.
  const [failed, setFailed] = useState(false);
  return (
    // The message stacks under the button rather than beside it. Beside it, the widest
    // state of this column is the error rather than the button, and the table is already
    // at its min-width — so one failed resolve took the width out of every other column
    // in the table and wrapped the weights and the labels.
    <div className="inline-flex flex-col items-end gap-1">
      <Button
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setFailed(false);
          const res = await fetch(`${BASE}/exceptions/${eventId}/resolve`, {
            method: "POST",
            headers,
          });
          setBusy(false);
          if (!res.ok) {
            setFailed(true);
            return;
          }
          onResolved();
        }}
      >
        {busy ? "Resolving…" : failed ? "Retry" : "Resolve"}
      </Button>
      {failed && (
        <span role="alert" className="whitespace-nowrap text-xs text-red-600">
          Could not resolve
        </span>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "emerald" | "orange" | "red";
}) {
  return (
    <div className="rounded-lg border border-black/[0.06] bg-white px-4 py-3.5">
      <div className="text-xs text-gray-500">{label}</div>
      <div
        className={cn(
          "mt-1.5 text-lg font-semibold tabular-nums text-gray-900",
          tone === "emerald" && "text-emerald-700",
          tone === "orange" && "text-orange-600",
          tone === "red" && "text-red-600",
        )}
      >
        {value}
        {hint && <span className="ml-1.5 text-xs font-normal text-gray-400">{hint}</span>}
      </div>
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-8">
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        <span className="text-xs text-gray-400">{note}</span>
      </div>
      {children}
    </section>
  );
}

function Progress({ value, target }: { value: number; target: number }) {
  const pctDone = Math.min(100, (value / target) * 100);
  return (
    <div className="w-28">
      <div className="mb-1 flex justify-between text-xs tabular-nums text-gray-500">
        <span>
          {value} / {target}
        </span>
        <span>{pctDone.toFixed(0)}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={cn("h-full rounded-full", value >= target ? "bg-emerald-600" : "bg-blue-600")}
          style={{ width: `${pctDone}%` }}
        />
      </div>
    </div>
  );
}

/**
 * The verdict split as one bar — the mock's Split column.
 *
 * `aria-hidden` because the rates it draws are already in the row as text a few columns
 * to the left; announcing them again as an unlabelled graphic adds noise, not
 * information. A period with no work has null rates, and zero-width segments would read
 * as real data, so it shows the same em dash the rate columns do.
 *
 * The can't-verify segment is what keeps the bar honest: the shares share one denominator,
 * so leaving flags out would draw a bar that stops short of full with nothing explaining
 * the gap.
 *
 * POS2-1206: five segments for five disjoint shares. Correct2 — the relabels that only
 * refined the model's own label — is the lighter emerald beside Correct, and since those
 * rows also left the relabel counter, the orange is what the model actually got wrong.
 * Nothing is derived here: each width is a share the aggregate already computed.
 */
function Split({
  correct,
  correct2,
  relabel,
  discard,
  cantVerify,
}: {
  correct: number | null;
  correct2: number | null;
  relabel: number | null;
  discard: number | null;
  cantVerify: number | null;
}) {
  if (correct === null && relabel === null && discard === null && cantVerify === null) {
    return <span className="text-gray-300">—</span>;
  }
  return (
    <div aria-hidden="true" className="flex h-1.5 w-32 overflow-hidden rounded-full bg-gray-100">
      <div className="bg-emerald-600" style={{ width: `${correct ?? 0}%` }} />
      <div className="bg-emerald-300" style={{ width: `${correct2 ?? 0}%` }} />
      <div className="bg-orange-500" style={{ width: `${relabel ?? 0}%` }} />
      <div className="bg-red-600" style={{ width: `${discard ?? 0}%` }} />
      <div className="bg-gray-400" style={{ width: `${cantVerify ?? 0}%` }} />
    </div>
  );
}

function Tag({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "emerald" | "orange" | "blue" | "neutral";
}) {
  return (
    <span
      className={cn(
        "inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium",
        tone === "emerald" && "bg-emerald-50 text-emerald-700",
        tone === "orange" && "bg-orange-50 text-orange-700",
        tone === "blue" && "bg-blue-50 text-blue-700",
        tone === "neutral" && "bg-gray-100 text-gray-600",
      )}
    >
      {children}
    </span>
  );
}

function Footnote({ children }: { children: React.ReactNode }) {
  return <p className="mt-2.5 max-w-4xl text-xs text-gray-400">{children}</p>;
}

const num = (value: number) => value.toLocaleString("en-GB");
// A null rate is "no data", never zero — see the file docstring.
const pct = (value: number | null) => (value === null ? "—" : `${value.toFixed(1)}%`);
const rate = (value: number | null, digits: number) =>
  value === null ? "—" : value.toFixed(digits);
// POS2-1206: Correct and Correct2 in one tile. A period with no work has both null, and
// "— / —" would read as two missing numbers rather than as no work, so it collapses to
// the single em dash every other tile shows.
const pctPair = (first: number | null, second: number | null) =>
  first === null && second === null ? pct(null) : `${pct(first)} / ${pct(second)}`;

function duration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * Dates and times on this screen are UTC, and say so where they are shown.
 *
 * Not a preference — it is the only self-consistent option. The queue's civil day is UTC
 * (`labelling_second_reviews.enqueued_on`), the period window is UTC (`_window()`), and the
 * subtitle names that window, so a column rendered in the viewer's own zone would put two
 * different days on one screen: a Dublin admin would read a Manila 08:05 clock-in as 01:05,
 * and a late Manila shift would show the previous day directly under a UTC subtitle.
 *
 * Whether the *right* zone is UTC or the labellers' own is a separate, open question — the
 * labelling day boundary lands at 08:00 in a UTC+8 working day, which is a product decision
 * rather than a formatting one. Changing it later is changing this constant, in one place.
 */
const DISPLAY_TIME_ZONE = "UTC";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    timeZone: DISPLAY_TIME_ZONE,
    day: "numeric",
    month: "short",
  });

/**
 * The period in words, from the window the server actually used.
 *
 * Rendered in UTC deliberately. `_window()` builds UTC days because labelling is one
 * global internal queue with no site or kitchen in scope, so there is no timezone chain to
 * resolve — and a viewer in a negative offset formatting those instants locally would see
 * the start date a day earlier than the figures beside it were computed from.
 *
 * `period_to` is exclusive and always "now", so its date is the last day included.
 */
function periodRange(fromIso: string, toIso: string): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  const format = (date: Date, options: Intl.DateTimeFormatOptions) =>
    date.toLocaleDateString("en-GB", { timeZone: DISPLAY_TIME_ZONE, ...options });

  const sameDay = format(from, { dateStyle: "short" }) === format(to, { dateStyle: "short" });
  if (sameDay) {
    // Composed rather than asked for in one call: en-GB renders the combination as
    // "Wednesday, 15 July 2026", and the mock has no comma. Composing also keeps the
    // string stable across ICU versions instead of depending on comma placement.
    const weekday = format(to, { weekday: "long" });
    return `${weekday} ${format(to, { day: "numeric", month: "long", year: "numeric" })}`;
  }

  const sameMonth =
    from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth();
  const end = format(to, { day: "numeric", month: "long", year: "numeric" });
  if (sameMonth) return `${from.getUTCDate()} to ${end}`;

  const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
  const start = format(from, {
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  return `${start} to ${end}`;
}

const clockTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-GB", {
    timeZone: DISPLAY_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
  });
